/**
 * The short code of a CLI browser session (nobgp-router#1859), for `nobgp login` and
 * `nobgp register` both.
 *
 * The terminal prints `app.nobgp.com/activate` (a login) or `/register` and a 4-character code,
 * and its QR code carries `HTTPS://APP.NOBGP.COM/ACTIVATE/<CODE>`. One page
 * (`components/CliCode.tsx`) takes the code at both routes.
 *
 * ⚠⚠ A login code is NEVER typed at `/login` (Benoit, 2026-09-27). `/login` is the app's own
 * sign-in page, and it sends a signed-in reader straight to the app: the code could not be typed
 * there, and changing that page would change every user's sign-in. The router's `POST /v1/code` turns it into the session, and the page then goes
 * on to what each flow already had: the router's sign-in confirmation for a login, the approval
 * form for a registration.
 *
 * ⚠⚠ THE CODE IS AN INPUT, and the one who enters it approves the machine into THEIR network, or
 * signs the terminal in as THEMSELVES. The router limits wrong codes and answers every miss the
 * same way. The page must not add a second answer: it says one sentence for every 404.
 */

export type CliKind = 'login' | 'register'

/** The length of a code. The router keeps its own copy (`regsession.UserCodeLength`). */
export const CODE_LENGTH = 4

/**
 * The code the way the router stores it, from the way a person typed it: uppercase, no spaces or
 * dashes, `0` read as `O` and `1` as `I` (the alphabet is A–Z and 2–7). `''` when what is left
 * cannot be a code. Same rule as the router's `regsession.NormalizeCode`.
 */
export const normalizeCode = (typed: string | null | undefined): string => {
  let out = ''
  for (const ch of (typed ?? '').toUpperCase()) {
    if (ch === ' ' || ch === '-') continue
    const c = ch === '0' ? 'O' : ch === '1' ? 'I' : ch
    if (!/^[A-Z2-7]$/.test(c)) return ''
    out += c
  }
  return out.length === CODE_LENGTH ? out : ''
}

/**
 * What the code field keeps while the person types: the same reading as `normalizeCode`, but a
 * partial code is kept, and a character that cannot be in a code is dropped rather than refused.
 */
export const typingCode = (typed: string): string => {
  let out = ''
  for (const ch of typed.toUpperCase()) {
    const c = ch === '0' ? 'O' : ch === '1' ? 'I' : ch
    if (/^[A-Z2-7]$/.test(c)) out += c
  }
  return out.slice(0, CODE_LENGTH)
}

/** The app page where a code of each kind is typed. Same table as the router's `codePage`. */
export const CODE_PAGE: Record<CliKind, string> = { login: '/activate', register: '/register' }

/** The page's own path for a code, so a sign-in comes back to it. */
export const codePath = (kind: CliKind, code: string): string =>
  code ? `${CODE_PAGE[kind]}/${code}` : CODE_PAGE[kind]

/**
 * Where the browser goes after the router found the session.
 *
 * - A login goes on to the ROUTER's `/auth/login?auth_session=`, which signs in with AuthKit and
 *   shows the confirmation (Deny / Continue). Only that leg can give the terminal a refresh token:
 *   it is the router's own AuthKit code exchange.
 * - A registration goes on to the approval form, which reads the session by id. `?session=` is
 *   the page's own address now, never a printed one: the router binds the session to this account
 *   at the lookup, so the id is worth nothing to anybody else, and a reload keeps working.
 */
export const afterCode = (
  kind: CliKind,
  sessionId: string,
  routerBaseUrl: string
): { href: string } | { path: string } =>
  kind === 'login'
    ? { href: `${routerBaseUrl.replace(/\/+$/, '')}/auth/login?auth_session=${encodeURIComponent(sessionId)}` }
    : { path: `/register?session=${encodeURIComponent(sessionId)}` }

/**
 * ONE sentence for every miss, so it names every cause (app#418): the router does not say which
 * it was. "Another browser" is the common surprise: the browser the terminal opened, signed in as
 * another account, claims the code before the phone does. The terminal names that account.
 */
export const CODE_MISS_MESSAGE =
  'That code cannot be used. It is wrong, it expired, or another browser already entered it. Run the command again to get a new code.'
export const CODE_LIMIT_MESSAGE = 'Too many wrong codes. Wait a minute and try again.'
export const CODE_UNAVAILABLE_MESSAGE = 'The code cannot be checked now. Try again in a minute.'

export type CodeFailure = { kind: 'miss' | 'limit' | 'unauthenticated' | 'unavailable'; message: string }

/** One sentence per status. Every 404 is the same miss, as the router means it. */
export const codeFailure = (status: number): CodeFailure => {
  switch (status) {
    case 404:
      return { kind: 'miss', message: CODE_MISS_MESSAGE }
    case 429:
      return { kind: 'limit', message: CODE_LIMIT_MESSAGE }
    case 401:
      return { kind: 'unauthenticated', message: 'Your sign-in has expired. Sign in again.' }
    default:
      return { kind: 'unavailable', message: CODE_UNAVAILABLE_MESSAGE }
  }
}

/** The session the router answered, or null when the answer is not one. */
export const parseCodeAnswer = (json: unknown, kind: CliKind): string | null => {
  if (!json || typeof json !== 'object') return null
  const { session, kind: answered } = json as { session?: unknown; kind?: unknown }
  if (typeof session !== 'string' || !session || answered !== kind) return null
  return session
}
